// @flow

import React from "react";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { TitleSection, Button, Loader, RightPanel, View, Text } from "@components";
import { route } from "@routes/helper";
import actionCreators from "@modules/reporting/actionCreator";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import type { State } from "@store/reducers";
import { getCurrentPath } from "@selectors";
import { selectUserType } from "@modules/account/selectors";
import { isLoading } from "@modules/reporting/selectors";
import {
  HelpPanelTasks,
  HelpPanelDashboard,
  HelpPanelActivities,
} from "@modules/reporting/components";
import * as loginSelectors from "@modules/account/selectors";

import contextActionCreators from "@modules/context/actionCreator";
import * as accountSelectors from "@modules/account/selectors";
import type { ProfileClient } from "@modules/account/types";
import { getClientContext } from "@modules/context";
import type { Client } from "@modules/context/types";

type PropsFromState = $Exact<{
  currentPath: string,
  userType: string,
  userClient: ProfileClient,
  client: Client | null,
  isLoading: boolean,
  usersDisplayName: string,
  title?: string,
}>;

type PropsFromDispatch = $Exact<{
  getToolsDashboard: typeof actionCreators.GetToolsDashboard,
  setClientContext: typeof contextActionCreators.SetClientContext,
  getAbsenceDefaultReports: typeof actionCreators.GetAbsenceDefaultReports,
  push: typeof push,
}>;

type Props = {
  push: typeof push,
  children: any,
  clientId: number,
  subTitle?: string,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromPermissions,
  ...PropsFromState,
};

type OwnState = $Exact<{
  isPanelOpened: boolean,
}>;

class DashboardTitleSection extends React.Component<Props, OwnState> {
  state = {
    isPanelOpened: false,
  };

  componentDidMount() {
    if (!this.props.client) {
      this.props.setClientContext(this.props.userClient);
    }
  }

  get sectionTitle() {
    if (this.props.currentPath.includes("dashboard")) {
      return this.props.t("DASHBOARD");
    }
    if (this.props.currentPath.includes("activityfeed")) {
      return this.props.t("ACTIVITIES");
    }
    if (this.props.currentPath.includes("tasks")) {
      return this.props.t("TASKS");
    }
    if (this.props.currentPath.includes("notifications")) {
      return this.props.t("NOTIFICATIONS");
    }
    if (this.props.currentPath.includes("coronavirus")) {
      return this.props.t("COVID_19_TRACKER");
    }

    return this.props.title || this.props.t("BENCHMARKING_INDEX");
  }

  get isDashboard() {
    return this.props.currentPath.includes("dashboard");
  }

  get isHelpPanelEnabled() {
    return (
      this.props.currentPath.includes("tasks") ||
      this.props.currentPath.includes("dashboard") ||
      this.props.currentPath.includes("activityfeed")
    );
  }

  handleRedirect = (path: string) => () => this.props.push(path);

  toggleRightPanel = () => {
    this.setState({ isPanelOpened: !this.state.isPanelOpened });
  };

  get helpPanel() {
    // all pages with dashboard title section have '/' in their path
    const pathParts = this.props.currentPath.split("/");
    const currentPage = pathParts[pathParts.length - 1];

    switch (currentPage) {
      case "tasks":
        return <HelpPanelTasks />;
      case "activityfeed":
        return <HelpPanelActivities />;
      default:
        return <HelpPanelDashboard />;
    }
  }

  render() {
    return (
      <View>
        {this.props.isLoading && !this.isDashboard && <Loader />}
        <View direction="row" alignItems="center">
          <Text type="h4" className="heading" text={this.sectionTitle} />
          {this.isHelpPanelEnabled && (
            <Button className="ml-3" onClick={this.toggleRightPanel} materialIcon="help">
              {this.props.t("HELP")}
            </Button>
          )}
        </View>

        {this.props.subTitle ? <div className="sub-title">{this.props.subTitle}</div> : null}
        {this.isHelpPanelEnabled && this.state.isPanelOpened && (
          <RightPanel title={this.sectionTitle} onClose={this.toggleRightPanel}>
            {this.helpPanel}
          </RightPanel>
        )}
        {this.props.children}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  usersDisplayName: loginSelectors.getUserName(state),
  currentPath: getCurrentPath(state),
  userType: selectUserType(state),
  userClient: accountSelectors.getUserClient(state),
  client: getClientContext(state),
  isLoading: isLoading(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getToolsDashboard: actionCreators.GetToolsDashboard,
  setClientContext: contextActionCreators.SetClientContext,
  getAbsenceDefaultReports: actionCreators.GetAbsenceDefaultReports,
  push,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({ permissionId: PermissionEnum.ACCESS_REPORTING_TOOLS }),
)(DashboardTitleSection);
